import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getAdminSeo, saveSeo, deleteSeo, type SeoPayload } from "@/services/adminService";
import type { AdminSeoSetting } from "@/types/admin";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import AdminSearchInput from "@/pages/portal/admin/components/AdminSearchInput";
import AdminModal from "@/pages/portal/admin/components/AdminModal";
import {
  adminCard,
  adminBtnPrimary,
  adminBtnGhost,
  adminBtnDanger,
  adminIconBtn,
  adminInput,
  adminLabel,
} from "@/pages/portal/admin/config/ui";

const EMPTY: SeoPayload = {
  route: "", pageLabel: "", title: "", description: "", keywords: "", ogTitle: "", ogDescription: "", noindex: false,
};

export default function SeoSection() {
  const { accessToken } = useAuth();
  const { data, setData, loading, error, reload } = useAdminResource(
    () => getAdminSeo(accessToken),
    [] as AdminSeoSetting[],
    [accessToken],
  );

  const [search, setSearch] = useState("");
  const [form, setForm]     = useState<SeoPayload | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminSeoSetting | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((s) => [s.route, s.pageLabel, s.title].join(" ").toLowerCase().includes(q));
  }, [data, search]);

  function openCreate() { setForm({ ...EMPTY }); setEditId(null); }
  function openEdit(row: AdminSeoSetting) {
    setForm({
      route: row.route, pageLabel: row.pageLabel, title: row.title, description: row.description,
      keywords: row.keywords, ogTitle: row.ogTitle, ogDescription: row.ogDescription, noindex: row.noindex,
    });
    setEditId(row.id);
  }
  function setField<K extends keyof SeoPayload>(key: K, value: SeoPayload[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function submit() {
    if (!form?.route?.trim()) return;
    setBusyId(editId ?? -1);
    try {
      await saveSeo(accessToken, form, editId ?? undefined);
      if (editId) {
        setData((prev) => prev.map((s) => (s.id === editId ? { ...s, ...form } : s)));
      } else {
        setData((prev) => [
          ...prev,
          {
            id: Date.now(), route: form.route as string, pageLabel: form.pageLabel ?? "",
            title: form.title ?? "", description: form.description ?? "", keywords: form.keywords ?? "",
            ogTitle: form.ogTitle ?? "", ogDescription: form.ogDescription ?? "",
            noindex: form.noindex ?? false, updatedAt: new Date().toISOString(),
          },
        ]);
      }
      setForm(null);
      setEditId(null);
    } finally {
      setBusyId(null);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await deleteSeo(accessToken, deleteTarget.id);
      setData((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      setDeleteTarget(null);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <AdminSearchInput value={search} onChange={setSearch} placeholder="Search routes…" />
        <button type="button" onClick={openCreate} className={adminBtnPrimary}>
          <i className="ri-add-line text-sm" /> New page rule
        </button>
        <span className="ml-auto text-xs text-foreground-500">{loading ? "Loading…" : `${data.length} page rules`}</span>
      </div>

      {error && <AdminDataError message={error} onRetry={reload} />}

      <div className="space-y-3">
        {loading ? (
          <div className={`${adminCard} flex items-center justify-center py-16`}>
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className={`${adminCard} flex flex-col items-center justify-center py-16 text-center`}>
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-search-eye-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700">No SEO rules yet</p>
          </div>
        ) : (
          filtered.map((s) => (
            <div key={s.id} className={`${adminCard} p-5`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="rounded bg-background-100 px-2 py-0.5 text-[11px] font-semibold text-foreground-700">{s.route}</code>
                    <span className="text-xs text-foreground-500">{s.pageLabel}</span>
                    {s.noindex && (
                      <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700">noindex</span>
                    )}
                  </div>
                  <p className="mt-2 text-sm font-semibold text-foreground-900">{s.title}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{s.description}</p>
                  <p className="mt-1.5 text-[11px] text-foreground-400">
                    <span className={`font-semibold ${s.title.length > 60 ? "text-rose-600" : "text-emerald-600"}`}>{s.title.length}</span> title ·
                    <span className={`ml-1 font-semibold ${s.description.length > 160 ? "text-rose-600" : "text-emerald-600"}`}>{s.description.length}</span> description
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button type="button" onClick={() => openEdit(s)} className={adminIconBtn} title="Edit"><i className="ri-pencil-line text-base" /></button>
                  <button type="button" onClick={() => setDeleteTarget(s)} className={adminIconBtn} title="Delete"><i className="ri-delete-bin-line text-base text-rose-600" /></button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {form && (
        <AdminModal
          title={editId ? "Edit SEO rule" : "New SEO rule"}
          onClose={() => { setForm(null); setEditId(null); }}
          wide
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setForm(null); setEditId(null); }} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={!form.route?.trim() || busyId !== null} onClick={submit} className={adminBtnPrimary}>
                {editId ? "Save changes" : "Create rule"}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={adminLabel} htmlFor="seo-route">Route</label>
                <input id="seo-route" type="text" value={form.route ?? ""} onChange={(e) => setField("route", e.target.value)} placeholder="/category/:slug" className={adminInput} />
              </div>
              <div>
                <label className={adminLabel} htmlFor="seo-label">Page label</label>
                <input id="seo-label" type="text" value={form.pageLabel ?? ""} onChange={(e) => setField("pageLabel", e.target.value)} placeholder="Category page" className={adminInput} />
              </div>
            </div>
            <div>
              <label className={adminLabel} htmlFor="seo-title">Meta title <span className="ml-1 font-normal normal-case text-foreground-400">({(form.title ?? "").length}/60)</span></label>
              <input id="seo-title" type="text" value={form.title ?? ""} onChange={(e) => setField("title", e.target.value)} maxLength={70} className={adminInput} />
            </div>
            <div>
              <label className={adminLabel} htmlFor="seo-desc">Meta description <span className="ml-1 font-normal normal-case text-foreground-400">({(form.description ?? "").length}/160)</span></label>
              <textarea id="seo-desc" rows={2} maxLength={200} value={form.description ?? ""} onChange={(e) => setField("description", e.target.value)} className={`${adminInput} h-auto py-2.5`} />
            </div>
            <div>
              <label className={adminLabel} htmlFor="seo-keywords">Keywords <span className="ml-1 font-normal normal-case text-foreground-400">(comma separated)</span></label>
              <input id="seo-keywords" type="text" value={form.keywords ?? ""} onChange={(e) => setField("keywords", e.target.value)} className={adminInput} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={adminLabel} htmlFor="seo-ogt">OG title</label>
                <input id="seo-ogt" type="text" value={form.ogTitle ?? ""} onChange={(e) => setField("ogTitle", e.target.value)} className={adminInput} />
              </div>
              <div>
                <label className={adminLabel} htmlFor="seo-ogd">OG description</label>
                <input id="seo-ogd" type="text" value={form.ogDescription ?? ""} onChange={(e) => setField("ogDescription", e.target.value)} className={adminInput} />
              </div>
            </div>
            <label className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground-700">
              <input type="checkbox" checked={!!form.noindex} onChange={(e) => setField("noindex", e.target.checked)} className="h-4 w-4 cursor-pointer accent-primary-500" />
              Exclude this page from search engines (noindex)
            </label>
            <p className="text-xs text-foreground-500">
              <i className="ri-information-line mr-1" />
              Tokens like <code className="rounded bg-background-100 px-1">{"{Category}"}</code> are replaced at render time with the real value.
            </p>
          </div>
        </AdminModal>
      )}

      {deleteTarget && (
        <AdminModal
          title="Delete SEO rule"
          subtitle={deleteTarget.route}
          onClose={() => setDeleteTarget(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteTarget(null)} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={busyId === deleteTarget.id} onClick={confirmDelete} className={adminBtnDanger}>
                <i className="ri-delete-bin-line text-sm" /> Delete
              </button>
            </div>
          }
        >
          <p className="text-sm text-foreground-700">Delete the SEO rule for <strong>{deleteTarget.route}</strong>?</p>
        </AdminModal>
      )}
    </div>
  );
}